import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  DataTable,
  EmptyTableState,
  ErrorState,
  useApiClient,
  type DataTableColumn,
} from '@startupquickstart/frontend';
import { formatDistanceToNow } from 'date-fns';
import { Check, X, Clock, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export type RemovalRequestStatus = 'pending' | 'approved' | 'rejected';
export type RemovalRequestFilter = RemovalRequestStatus | 'processed';

export interface PhotoRemovalRequest {
  id: string;
  photoSubmissionId: string;
  userId: string;
  reason: string;
  status: RemovalRequestStatus;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  adminNotes?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  } | null;
  photoSubmission?: {
    id: string;
    entityType: string;
    entityId: string;
    entityName?: string | null;
    status: string;
    media?: {
      id: string;
      fileName: string;
      fileType: string;
      url?: string;
    } | null;
  } | null;
  reviewer?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  } | null;
}

interface RemovalRequestsListProps {
  status?: RemovalRequestFilter;
}

function StatusBadge({ status }: { status: RemovalRequestStatus }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
    case 'approved':
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          <Check className="mr-1 h-3 w-3" />
          Approved
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          <X className="mr-1 h-3 w-3" />
          Rejected
        </Badge>
      );
    default:
      return null;
  }
}

function getUserName(user?: PhotoRemovalRequest['user']) {
  if (!user) return 'Unknown';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email;
}

export function RemovalRequestsList({ status }: RemovalRequestsListProps) {
  const apiClient = useApiClient();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['photo-removal-requests', status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status === 'processed') {
        // Processed means approved OR rejected (not pending)
        params.set('filters[status]', 'approved,rejected');
      } else if (status) {
        params.set('filters[status]', status);
      }
      const response = (await apiClient.get(`/photo-removal-requests?${params.toString()}`)) as {
        data: PhotoRemovalRequest[] | { data: PhotoRemovalRequest[] };
      };
      return response.data;
    },
  });

  // Handle both wrapped ({ data: [...] }) and unwrapped ([...]) response formats
  const requests: PhotoRemovalRequest[] = Array.isArray(data) ? data : (data?.data || []);

  const handleRowClick = (request: PhotoRemovalRequest) => {
    navigate(`/photo-submissions/removal-requests/${request.id}`);
  };

  // Define columns for DataTable
  const columns: DataTableColumn<PhotoRemovalRequest>[] = [
    {
      key: 'photoSubmission' as keyof PhotoRemovalRequest,
      label: 'Photo',
      format: (_, request) => (
        request.photoSubmission?.media?.url ? (
          <img
            src={request.photoSubmission.media.url}
            alt="Submitted photo"
            className="h-12 w-12 rounded-md object-cover"
          />
        ) : (
          <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center">
            <Camera className="h-5 w-5 text-muted-foreground" />
          </div>
        )
      ),
    },
    {
      key: 'photoSubmission' as keyof PhotoRemovalRequest,
      label: 'Entity',
      format: (_, request) => (
        <div>
          <p className="font-medium">
            {request.photoSubmission?.entityName || request.photoSubmission?.entityId || 'Unknown Entity'}
          </p>
          <p className="text-sm text-muted-foreground capitalize">
            {request.photoSubmission?.entityType}
          </p>
        </div>
      ),
    },
    {
      key: 'user' as keyof PhotoRemovalRequest,
      label: 'Requested By',
      format: (_, request) => (
        <div>
          <p className="font-medium">{getUserName(request.user)}</p>
          <p className="text-sm text-muted-foreground">
            {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
          </p>
        </div>
      ),
      hideOnMobile: true,
    },
    {
      key: 'reason',
      label: 'Reason',
      format: (reason) => (
        <p className="text-sm max-w-xs truncate" title={reason}>
          {reason}
        </p>
      ),
      hideOnMobile: true,
    },
    {
      key: 'status',
      label: 'Status',
      format: (status) => <StatusBadge status={status} />,
    },
  ];

  // Show error state
  if (error) {
    return (
      <ErrorState
        title="Failed to load removal requests"
        error={error}
        action={{
          label: 'Try again',
          onClick: () => refetch(),
        }}
      />
    );
  }

  return (
    <DataTable
      columns={columns}
      data={requests}
      loading={isLoading}
      onRowClick={handleRowClick}
      emptyState={
        <EmptyTableState
          icon={<Camera className="h-12 w-12" />}
          title="No removal requests"
          description={status === 'pending'
            ? "There are no pending removal requests to review"
            : "No removal requests found"}
        />
      }
      skeletonRows={5}
    />
  );
}
